import React, { useState, useEffect } from "react";
import {
  Button,
  CircularProgress,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import { uploadCompanyLogo } from "../../utils/firebaseStorage";
import { getCompanyData } from "../../utils/generalUtil";
import { useCompanyLogo } from "../../hooks/useCompanyLogo";
import CloseIcon from "@mui/icons-material/Close";

interface FileUploadProps {
  onUploadSuccess: (url: string) => void;
  onUploadError: (error: string) => void;
  buttonText?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onUploadSuccess,
  onUploadError,
  buttonText = "Upload Logo",
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [currentLogoUrl, setCurrentLogoUrl] = useState<string | null>(null);

  const companyData = getCompanyData();
  const { logoUrl: fetchedLogoUrl, loading: logoLoading } = useCompanyLogo(
    companyData?.companyId
  );

  useEffect(() => {
    if (fetchedLogoUrl) {
      setCurrentLogoUrl(fetchedLogoUrl);
    }
  }, [fetchedLogoUrl]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      onUploadError("Por favor, selecione apenas arquivos de imagem.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      onUploadError("O arquivo deve ter menos de 5MB.");
      return;
    }

    setSelectedFile(file);

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    setLoading(true);

    try {
      const companyData = getCompanyData();
      const companyId = companyData?.companyId;

      if (!companyId) {
        throw new Error("ID da empresa não encontrado.");
      }

      const downloadURL = await uploadCompanyLogo(selectedFile, companyId);

      setCurrentLogoUrl(downloadURL);

      onUploadSuccess(downloadURL);
      clearPreview();
    } catch (error: any) {
      console.error("Erro no upload:", error);
      onUploadError(error.message || "Erro ao fazer upload do arquivo.");
    } finally {
      setLoading(false);
    }
  };

  const clearPreview = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        {logoLoading ? (
          <Box
            sx={{
              width: "200px",
              height: "200px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              border: "1px solid #ddd",
            }}
          >
            <CircularProgress size={20} />
          </Box>
        ) : currentLogoUrl ? (
          <Box sx={{ position: "relative", display: "inline-block" }}>
            <img
              src={currentLogoUrl}
              alt="Logo da empresa"
              style={{
                maxWidth: "200px",
                maxHeight: "200px",
                objectFit: "contain",
                border: "1px solid #ddd",
                borderRadius: "8px",
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              width: "200px",
              height: "200px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              border: "1px solid #ddd",
            }}
          >
            <Typography variant="body2" color="textSecondary">
              Nenhuma logo definida
            </Typography>
          </Box>
        )}
      </Box>
      {previewUrl && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="body2" color="textSecondary">
            Nova Logo (Preview)
          </Typography>
          <Box sx={{ position: "relative", display: "inline-block" }}>
            <img
              src={previewUrl}
              alt="Preview da nova logo"
              style={{
                maxWidth: "200px",
                maxHeight: "200px",
                objectFit: "contain",
                border: "1px solid #ddd",
                borderRadius: "8px",
              }}
            />
            <IconButton
              onClick={clearPreview}
              sx={{
                position: "absolute",
                top: -8,
                right: -8,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 1)",
                },
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <input
          accept="image/*"
          style={{ display: "none" }}
          id="file-upload"
          type="file"
          onChange={handleFileSelect}
          disabled={loading}
        />
        <label htmlFor="file-upload">
          <Button
            variant="contained"
            component="span"
            disabled={loading}
            sx={{
              background: "#1976d2",
              "&:hover": {
                background: "#1565c0",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Typography variant="body2">
                {selectedFile ? "Selecionar outro arquivo" : buttonText}
              </Typography>
            )}
          </Button>
        </label>

        {selectedFile && !loading && (
          <Button
            variant="contained"
            onClick={handleFileUpload}
            sx={{
              background: "#4caf50",
              "&:hover": {
                background: "#45a049",
              },
            }}
          >
            <Typography variant="body2">Confirmar Upload</Typography>
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default FileUpload;
